import { createElement } from 'react';
import { StoreApi } from 'zustand';

import { createRoot, GraphRenderer } from '@spazious/dollhouse-fiber';
import { ApolloClient, ApolloProvider } from '@spazious/dollhouse-graphql/apollo';
import { FShallowGraph } from '@spazious/dollhouse-shallow-graph';

import { ErrorBoundary } from '../components/ErrorBoundary/index.js';
import { FSetup as FSetupFunctionalComponent, GraphProvider } from '../components/index.js';
import { NodeLayers } from '../core/NodeLayers.js';
import { BaseNodeImpl } from '../nodes/BaseNode.js';
import { createFSetupStore, FSetupStore } from '../store/index.js';
import { FNodeRoot, FSetupFetchMethodFunction } from '../types/index.js';

export class FSetupGraph<TCache = any> extends BaseNodeImpl implements FNodeRoot {
  renderer: GraphRenderer;
  store: FSetupStore;
  element: React.ReactElement;
  /**
   * Layers of the graph for enabling and disabling nodes by not adding them to the graph
   */
  layers = new NodeLayers();

  shallow: FShallowGraph = null!;

  constructor(
    fetchMethod: FSetupFetchMethodFunction,
    client: ApolloClient<TCache>,
    shallow: StoreApi<FShallowGraph>,
    onError?: (err?: Error) => void
  ) {
    super('fsetup');

    this.renderer = createRoot(this as any);

    this.layers.enableAll();

    this.store = createFSetupStore(fetchMethod, onError);
    this.store.setState({
      fetchMethod,
    });

    this.element = createElement(ErrorBoundary, {
      onError: onError ?? ((err?: Error) => console.error(err)),
      children: createElement(ApolloProvider, {
        client,
        children: createElement(GraphProvider, {
          graph: this,
          children: createElement(FSetupFunctionalComponent),
        }),
      }),
    });

    this.renderer.render(this.element);

    shallow.subscribe((graph) => {
      this.store.setState({ graph });
    });

    // Defined as getter/setter to prevent accidental mutation from base class BaseNodeImpl
    Object.defineProperty(this, 'shallow', {
      get() {
        return this.store.getState().graph;
      },
      set() {
        throw new Error('Use setShallowGraph instead');
      },
    });
  }

  setShallowGraph(graph: FShallowGraph) {
    this.store.setState({ graph });
  }

  resetShallowGraph() {
    this.store.setState((state) => ({
      ...state,
      graph: {
        ...state.graph,
        nodes: {},
      },
    }));
  }

  checkCollisions() {
    this.emit('checkCollisions');
  }

  flushCollisions() {
    this.emit('flushCollisions');
  }

  toJSON(recursive?: boolean) {
    return {
      ...super.toJSON(recursive),
      shallow: this.shallow,
    };
  }
}

export const createFSetupGraph = <TCache = any>(
  fetchMethod: FSetupFetchMethodFunction,
  client: ApolloClient<TCache>,
  shallow: StoreApi<FShallowGraph>,
  onError?: (err?: Error) => void
) => {
  return new FSetupGraph(fetchMethod, client, shallow, onError);
};
